import { useId } from "react";
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from "react";
import { useT } from "@/contexts/I18nContext";

/**
 * A labelled form control.
 *
 * The label is bound with a generated id rather than by wrapping, so screen
 * readers announce it and a click on the label focuses the input. `errorKey` is
 * an i18n key so a validation message from the backend renders identically to
 * one produced here.
 */

interface Common {
  /** i18n key for the label. */
  labelKey: string;
  /** i18n key for help text under the control. */
  hintKey?: string;
  errorKey?: string;
  errorParams?: Record<string, unknown>;
  required?: boolean;
}

function Shell({
  labelKey,
  hintKey,
  errorKey,
  errorParams,
  required,
  id,
  children,
}: Common & { id: string; children: ReactNode }) {
  const t = useT();
  return (
    <div>
      <label className="field-label" htmlFor={id}>
        {t(labelKey)}
        {/*
          Hidden from the accessibility tree. The asterisk is a visual
          convention; a screen reader announcing "Password star" is noise, and
          the `required` attribute on the control already says it properly.
        */}
        {required ? (
          <span className="text-danger" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
      </label>
      {children}
      {hintKey ? <p className="mt-1.5 text-[13px] text-ink-muted">{t(hintKey)}</p> : null}
      {errorKey ? (
        <p className="field-error" role="alert">
          {t(errorKey, errorParams)}
        </p>
      ) : null}
    </div>
  );
}

export type TextFieldProps = Common &
  Omit<InputHTMLAttributes<HTMLInputElement>, "id" | "required">;

export function TextField({
  labelKey,
  hintKey,
  errorKey,
  errorParams,
  required,
  className = "",
  ...rest
}: TextFieldProps) {
  const id = useId();
  return (
    <Shell
      id={id}
      labelKey={labelKey}
      hintKey={hintKey}
      errorKey={errorKey}
      errorParams={errorParams}
      required={required}
    >
      <input
        id={id}
        required={required}
        aria-invalid={errorKey ? true : undefined}
        className={`field-input ${className}`}
        {...rest}
      />
    </Shell>
  );
}

export type TextAreaFieldProps = Common &
  Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "id" | "required">;

export function TextAreaField({
  labelKey,
  hintKey,
  errorKey,
  errorParams,
  required,
  className = "",
  ...rest
}: TextAreaFieldProps) {
  const id = useId();
  return (
    <Shell
      id={id}
      labelKey={labelKey}
      hintKey={hintKey}
      errorKey={errorKey}
      errorParams={errorParams}
      required={required}
    >
      <textarea
        id={id}
        required={required}
        aria-invalid={errorKey ? true : undefined}
        className={`field-input min-h-24 ${className}`}
        {...rest}
      />
    </Shell>
  );
}

export interface SelectOption {
  value: string;
  /** i18n key, translated here. */
  labelKey: string;
}

export type SelectFieldProps = Common &
  Omit<SelectHTMLAttributes<HTMLSelectElement>, "id" | "required" | "children"> & {
    options: readonly SelectOption[];
  };

export function SelectField({
  labelKey,
  hintKey,
  errorKey,
  errorParams,
  required,
  options,
  className = "",
  ...rest
}: SelectFieldProps) {
  const id = useId();
  const t = useT();
  return (
    <Shell
      id={id}
      labelKey={labelKey}
      hintKey={hintKey}
      errorKey={errorKey}
      errorParams={errorParams}
      required={required}
    >
      <select
        id={id}
        required={required}
        aria-invalid={errorKey ? true : undefined}
        className={`field-input ${className}`}
        {...rest}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {t(option.labelKey)}
          </option>
        ))}
      </select>
    </Shell>
  );
}
